import { useEffect, useCallback } from 'react'
import { WordGrid } from './WordGrid'
import { SongPicker } from './SongPicker'
import { HelpModal } from './HelpModal'
import { GospelSymbolsModal } from './GospelSymbolsModal'
import { SongControls } from './SongControls'
import { DisplayControls } from './DisplayControls'
import { WordControls } from './WordControls'
import { useUIStore } from '../stores/uiStore'
import { useSongStore } from '../stores/songStore'
import { useMainWindowPlatform } from '../platform/MainWindowPlatformProvider'

import { SongManager } from '../services/SongManager'
import { UrlUtils } from '../utils/urlUtils'
import { SongData } from '../types'
import '../styles/global.css'
import memtasticIcon from '/icons/memtastic-icon.svg?url'
import { AboutModal } from './AboutModal'
import { EmojiDictionaryModal } from './EmojiDictionaryModal'

const helpSeenKey = 'helpSeen'

export function MainWindow() {
	// UI State from UIStore - using individual selectors to avoid infinite re-renders
	const lyricsInput = useUIStore((state) => state.lyricsInput)
	const setLyricsInput = useUIStore((state) => state.setLyricsInput)
	const currentSongId = useUIStore((state) => state.currentSongId)
	const setCurrentSongId = useUIStore((state) => state.setCurrentSongId)
	const showSongPicker = useUIStore((state) => state.showSongPicker)
	const openSongPicker = useUIStore((state) => state.openSongPicker)
	const closeSongPicker = useUIStore((state) => state.closeSongPicker)
	const showHelpModal = useUIStore((state) => state.showHelpModal)
	const openHelpModal = useUIStore((state) => state.openHelpModal)
	const openAboutModal = useUIStore((state) => state.openAboutModal)
	const closeAboutModal = useUIStore((state) => state.closeAboutModal)
	const showAboutModal = useUIStore((state) => state.showAboutModal)
	const closeHelpModal = useUIStore((state) => state.closeHelpModal)
	const showGospelSymbolsModal = useUIStore((state) => state.showGospelSymbolsModal)
	const openGospelSymbolsModal = useUIStore((state) => state.openGospelSymbolsModal)
	const closeGospelSymbolsModal = useUIStore((state) => state.closeGospelSymbolsModal)
	const showEmojiDictionaryModal = useUIStore((state) => state.showEmojiDictionaryModal)
	const openEmojiDictionaryModal = useUIStore((state) => state.openEmojiDictionaryModal)
	const closeEmojiDictionaryModal = useUIStore((state) => state.closeEmojiDictionaryModal)

	// Display State from SongStore
	const currentPageIndex = useSongStore((state) => state.currentPageIndex)
	const showWordMode = useSongStore((state) => state.showWordMode)
	const setShowWordMode = useSongStore((state) => state.setShowWordMode)
	const hideWordMode = useSongStore((state) => state.hideWordMode)
	const setHideWordMode = useSongStore((state) => state.setHideWordMode)


	const fontSize = useSongStore((state) => state.fontSize)
	const setFontSize = useSongStore((state) => state.setFontSize)

	// Platform for display window management
	const platform = useMainWindowPlatform()

	// Song State Management from SongStore
	const songState = useSongStore((state) => state.songState)
	const hiddenPercentage = useSongStore((state) => state.hiddenPercentage)
	const toggleWord = useSongStore((state) => state.toggleWord)
	const shuffleWords = useSongStore((state) => state.shuffleWords)
	const updateHiddenPercentage = useSongStore((state) => state.updateHiddenPercentage)
	const loadSong = useSongStore((state) => state.loadSong)

	// Display Window Management from SongStore
	const isDisplayWindowOpen = useSongStore((state) => state.isDisplayWindowOpen)
	const openDisplayWindow = useSongStore((state) => state.openDisplayWindow)
	const closeDisplayWindow = useSongStore((state) => state.closeDisplayWindow)
	const syncToDisplay = useSongStore((state) => state.syncToDisplay)
	const handleDisplayMessage = useSongStore((state) => state.handleDisplayMessage)

	// Navigation handlers using SongStore actions
	const nextPage = useSongStore((state) => state.nextPage)
	const prevPage = useSongStore((state) => state.prevPage)

	const handleNextPage = useCallback(() => {
		nextPage()
	}, [nextPage])

	const handlePrevPage = useCallback(() => {
		prevPage()
	}, [prevPage])

	// Set up display window message handling
	useEffect(() => {
		const cleanup = platform.onMessageFromDisplay((message) => {
			if (message.type === 'DISPLAY_READY') {
				// Sync current state when display window is ready
				if (songState) {
					setTimeout(() => {
						syncToDisplay(platform)
					}, 100)
				}
			} else {
				handleDisplayMessage(message)
			}
		})
		return cleanup
	}, [platform, handleDisplayMessage, songState, syncToDisplay])

	// Load song from URL or show picker on mount
	useEffect(() => {
		// Check if this is the first visit
		const hasSeenHelp = localStorage.getItem(helpSeenKey)
		if (!hasSeenHelp) {
			openHelpModal()
		}

		const songIdFromUrl = UrlUtils.getSongIdFromUrl()
		if (songIdFromUrl) {
			const savedSong = SongManager.loadSong(songIdFromUrl)
			if (savedSong) {
				loadSong(savedSong)
				setLyricsInput(savedSong.lyrics)
				setCurrentSongId(songIdFromUrl)
			} else {
				// Song ID in URL but song doesn't exist, clear URL and show picker
				UrlUtils.clearSongIdFromUrl()
				openSongPicker()
			}
		} else {
			// No song in URL, show picker
			openSongPicker()
		}

		// Listen for URL changes (back/forward buttons)
		const cleanup = UrlUtils.listenForUrlChanges((songId) => {
			if (songId) {
				const song = SongManager.loadSong(songId)
				if (song) {
					loadSong(song)
					setLyricsInput(song.lyrics)
					setCurrentSongId(songId)
				}
			} else {
				setCurrentSongId(null)
				openSongPicker()
			}
		})

		return cleanup
	}, [])

	// Sync to display window when song or settings change
	useEffect(() => {
		if (songState && isDisplayWindowOpen) {
			syncToDisplay(platform)
		}
	}, [songState, currentPageIndex, hideWordMode, showWordMode, fontSize, syncToDisplay, platform, isDisplayWindowOpen])

	// Save song data when it changes
	useEffect(() => {
		if (songState) {
			// Only save the persistent data (without visibility state)
			const persistentData: SongData = {
				id: songState.id,
				title: songState.title,
				lyrics: songState.lyrics,
				lastModified: songState.lastModified,
			}
			SongManager.saveSong(persistentData)
		}
	}, [songState])

	// Keyboard event handling
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'ArrowRight') {
				handleNextPage()
			} else if (event.key === 'ArrowLeft') {
				handlePrevPage()
			} else if (event.key === 'ArrowUp') {
				updateHiddenPercentage(hiddenPercentage + 5)
			} else if (event.key === 'ArrowDown') {
				updateHiddenPercentage(hiddenPercentage - 5)
			} else if (event.key === 'Enter') {
				updateHiddenPercentage(hiddenPercentage === 0 ? 100 : 0)
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [handleNextPage, handlePrevPage, hiddenPercentage, updateHiddenPercentage])

	// Event handlers
	const handleLyricsSubmit = () => {
		if (!lyricsInput.trim() || !songState) return

		const updatedSongData: SongData = {
			id: songState.id,
			title: songState.title,
			lyrics: lyricsInput.trim(),
			lastModified: songState.lastModified,
		}

		loadSong(updatedSongData)
		SongManager.saveSong(updatedSongData)
	}

	const handleSelectSong = (songId: string) => {
		const song = SongManager.loadSong(songId)
		if (song) {
			loadSong(song)
			setLyricsInput(song.lyrics)
			setCurrentSongId(songId)
			UrlUtils.setSongIdInUrl(songId)
			closeSongPicker()
		}
	}

	const handleCloseHelp = () => {
		closeHelpModal()
		localStorage.setItem(helpSeenKey, 'true')
	}

	const currentSong = songState ? {
		id: songState.id,
		title: songState.title,
		lyrics: songState.lyrics,
		lastModified: songState.lastModified,
	} : null

	return (
		<div className="app">
			{/* <header className="app-header">
				<img src={memtasticIcon} alt="Memtastic Icon" /><h1>Memtastic</h1>
			</header> */}

			<div className="main-content">
				<div className="left-column">
					<SongControls
						lyricsInput={lyricsInput}
						onLyricsInputChange={setLyricsInput}
						onLyricsSubmit={handleLyricsSubmit}
						currentSong={currentSong}
						onShowSongPicker={openSongPicker}
						onShowEmojiDictionary={openEmojiDictionaryModal}
					/>

					<div className="controls-section">
						<DisplayControls
							isDisplayWindowOpen={isDisplayWindowOpen}
							onOpenDisplayWindow={() => openDisplayWindow(platform, songState?.title)}
							onCloseDisplayWindow={() => closeDisplayWindow(platform)}
							fontSize={fontSize}
							onFontSizeChange={setFontSize}
						/>

						{songState && (
							<WordControls
								hiddenPercentage={hiddenPercentage}
								onHiddenPercentageChange={updateHiddenPercentage}
								onShuffle={shuffleWords}
								showWordMode={showWordMode}
								onShowWordModeChange={setShowWordMode}
								hideWordMode={hideWordMode}
								onHideWordModeChange={setHideWordMode}
							/>
						)}
					</div>
				</div>

				<div className="right-column">
					{songState && (
						<div className="word-grid-section">
							<h3>Click words to toggle visibility:</h3>
							<WordGrid
								lines={songState.pages[currentPageIndex]}
								hideWordMode={hideWordMode}
								showWordMode={showWordMode}
								onWordToggle={toggleWord}
								fontSize={fontSize}

							/>
							<div className="page-navigation-controls">
								<button
									onClick={handlePrevPage}
									disabled={currentPageIndex === 0}
									className="btn btn-secondary"
								>
									Previous Page
								</button>
								<button
									onClick={handleNextPage}
									disabled={currentPageIndex === songState.pages.length - 1}
									className="btn btn-secondary"
								>
									Next Page
								</button>
							</div>
						</div>
					)}
				</div>
			</div>

			<footer className="app-footer">
				<div className="footer-links">
					<span className="footer-name"><img src={memtasticIcon} alt="Memtastic Icon" /> <h1>Memtastic</h1></span>
					<a href="#" onClick={(e) => { e.preventDefault(); openHelpModal() }} className="help-link">
						Help & Quick Tour
					</a>
					<a href="#" onClick={(e) => { e.preventDefault(); openGospelSymbolsModal() }} className="help-link">
						Gospel Symbols
					</a>
					<a href="#" onClick={(e) => { e.preventDefault(); openAboutModal() }} className="help-link">
						About
					</a>
				</div>
			</footer>

			<SongPicker
				isOpen={showSongPicker}
				onClose={() => { }}
				onSelectSong={handleSelectSong}
				currentSongId={currentSongId || undefined}
			/>

			<HelpModal
				isOpen={showHelpModal}
				onClose={handleCloseHelp}
			/>

			<GospelSymbolsModal
				isOpen={showGospelSymbolsModal}
				onClose={closeGospelSymbolsModal}
			/>

			<AboutModal
				isOpen={showAboutModal}
				onClose={closeAboutModal}
			/>

			<EmojiDictionaryModal
				isOpen={showEmojiDictionaryModal}
				onClose={closeEmojiDictionaryModal}
			/>
		</div>
	)
}
